import React, { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';

import classNames from 'classnames';
import { useTranslation } from 'react-i18next';

import { Estimate, Project } from 'entities/task/model/types/tasks';
import { useClickOutside } from 'shared/hooks';

import cls from './SearchSelectWithSubList.module.css';
import { Icon } from '../../../../shared/ui';
import { EstimateOption } from '../EstimateOption';

interface SearchSelectProps {
    selected: any;
    options: Project[];
    placeholder: string;
    onChange: (value: any) => void;
    title?: string;
    isRequired?: boolean;
    isDisabled?: boolean;
    error?: string | null;
    isAccount?: boolean;
    IconAddon?: React.ReactNode;
}

export const SearchSelectWithSubList = ({
    selected,
    options,
    placeholder,
    onChange,
    title,
    isRequired = false,
    isDisabled = false,
    error = null,
    IconAddon,
}: SearchSelectProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [optionFilter, setOptionFilter] = useState('');
    const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});

    const { t } = useTranslation();

    const { rootRef } = useClickOutside({ isOpen, setIsOpen, setOptionFilter });
    const noResultName = t('SearchSelectWithSubList.noResultName');

    const triggerRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);

    useEffect(() => {
        if (isOpen) {
            inputRef.current?.focus({ preventScroll: true });
        }
    }, [isOpen]);

    useLayoutEffect(() => {
        const calcRect = () => {
            if (!isOpen) {
                setDropdownStyle({});
                return;
            }

            if (!triggerRef.current) return;

            const rect = triggerRef.current.getBoundingClientRect();
            const spaceBelow = window.innerHeight - rect.bottom;
            const openUpward = spaceBelow < 170;

            setDropdownStyle({
                position: 'fixed',
                top: openUpward ? rect.top - 8 : rect.bottom + 4,
                left: rect.left,
                width: rect.width,
                transform: openUpward ? 'translateY(-100%)' : 'none',
            });
        };

        calcRect();

        window.addEventListener('resize', calcRect);
        window.addEventListener('scroll', calcRect, true);

        return () => {
            window.removeEventListener('resize', calcRect);
            window.removeEventListener('scroll', calcRect, true);
        };
    }, [isOpen]);

    const filteredOptions = useMemo(() => {
        if (!optionFilter) return options;

        const lowerFilter = optionFilter.toLowerCase();

        const filtered = options
            .map((item) => {
                // Проверяем совпадение в названии проекта
                const isProjectMatch = item.name.toLowerCase().includes(lowerFilter);

                return {
                    ...item,
                    // Для совпавших объектов оставляю все estimates
                    // Для остальных - фильтрую estimates
                    estimates: isProjectMatch
                        ? item.estimates
                        : item.estimates.filter((est) =>
                              est.name.toLowerCase().includes(lowerFilter)
                          ),
                };
            })
            .filter(
                (item) =>
                    // Оставляю объекты которые:
                    // 1. Имеют совпадение в названии ЛИБО
                    // 2. Имеют хотя бы один estimate после фильтрации
                    item.name.toLowerCase().includes(lowerFilter) || item.estimates.length > 0
            );

        return filtered.length > 0 ? filtered : [{ name: noResultName, id: null, estimates: [] }];
    }, [options, optionFilter]);

    const handleEstimateClick = (value: Estimate, projectId: number) => {
        setIsOpen(false);
        onChange({ ...value, type: 'estimate', projectId });
    };

    const handleProjectClick = (value: Project) => {
        console.log('handleObjectClick: ', value);

        if (value.name === noResultName) return;

        if (value.id === null) {
            onChange(value);
        }
        setIsOpen(false);
        onChange({ ...value, type: 'object' });
    };

    const toggleDropdown = () => {
        if (!isDisabled) {
            setOptionFilter('');
            setIsOpen((prevState) => !prevState);
        }
    };

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        setOptionFilter(event.target.value);
    };
    return (
        <div
            className={classNames(cls.formBlockContent, { [cls.disabled]: isDisabled })}
            ref={rootRef}
        >
            {title && (
                <label className={cls.modalLabelSelect}>
                    {title}
                    {isRequired && <span>*</span>}
                </label>
            )}

            <div
                onClick={toggleDropdown}
                className={classNames(
                    cls.selectWrapper,
                    { [cls.selectWrapperOpen]: isOpen },
                    { [cls.errorBorder]: error?.length > 0 },
                    { [cls.disabledWrapper]: isDisabled }
                )}
                ref={triggerRef}
                data-is-active={isOpen}
            >
                {selected?.name &&
                selected?.name != t('SearchSelectWithSubList.return.not_selected') ? (
                    <div className={cls.selected}>
                        <span
                            className={classNames(cls.selectedSpan, { [cls.disabled]: isDisabled })}
                        >
                            {selected.name}
                        </span>
                        {!isDisabled && (
                            <div
                                className={classNames(cls.arrow, {
                                    [cls.openArrow]: isOpen,
                                })}
                            >
                                <Icon iconName="selectArrow" fill="#818998" />
                            </div>
                        )}
                    </div>
                ) : (
                    <div className={cls.placeholderContainer}>
                        {IconAddon && IconAddon}
                        <div className={cls.placeholder}>{placeholder}</div>
                        {!isDisabled && (
                            <div
                                className={classNames(cls.arrow, {
                                    [cls.openArrow]: isOpen,
                                })}
                            >
                                <Icon iconName="selectArrow" fill="#818998" />
                            </div>
                        )}
                    </div>
                )}
            </div>
            {isOpen && (
                <ul className={cls.option} style={dropdownStyle}>
                    <div className={cls.filterInput}>
                        <input
                            ref={inputRef}
                            className={cls.input}
                            onChange={handleInputChange}
                            value={optionFilter}
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                    {filteredOptions?.map((option) => (
                        <Fragment key={option.id}>
                            <li
                                className={classNames(cls.Object, {
                                    [cls.disabled]: option.id === null,
                                })}
                                onClick={() => handleProjectClick(option)}
                            >
                                {option.name}
                            </li>
                            <EstimateOption
                                key={option.id}
                                projectId={option.id}
                                estimates={option.estimates}
                                onClick={handleEstimateClick}
                                setIsOpen={setIsOpen}
                                projectName={option.name}
                            />
                        </Fragment>
                    ))}
                </ul>
            )}
            {error && <span className={cls.errorMessage}>{error}</span>}
        </div>
    );
};
